import axiosInstance from '../../api/axios';

// Types mirror skeptics-backend/hygiene (store.go, query.go, ai_store.go) JSON tags verbatim.

export type HygieneSeverity = 'high' | 'medium' | 'low';
export type HygieneArea = 'planning' | 'process' | 'rcm' | 'itgc' | 'control' | 'testing' | 'issues' | 'evidence' | 'data';
export type HygieneState = 'open' | 'assigned' | 'pending_approval' | 'accepted' | 'fixed' | 'suggested' | 'rejected';
export type HygieneTab = 'open' | 'mine' | 'ai' | 'accepted' | 'fixed' | 'approval';
export type HygieneAction = 'assign' | 'accept' | 'approve' | 'reject' | 'reopen' | 'confirm' | 'dismiss';

export type HygieneFinding = {
  id: string;
  audit_year_id: string;
  finding_key: string;
  source: 'rules' | 'ai';
  check: string;
  area: HygieneArea;
  severity: HygieneSeverity;
  state: HygieneState;
  record_type: string;
  record_id: string;
  related_id?: string;
  record_code?: string;
  parent_code?: string;
  record_label: string;
  detail: string;
  acceptance_expired: boolean;
  ai_verdict?: 'fail' | 'unsure' | '';
  owner_user_id?: number;
  owner_label?: string;
  accept_reason?: string;
  accept_note?: string;
  requested_by?: number;
  first_seen_at: string;
  last_seen_at: string;
  state_changed_at: string;
  resolved_at?: string;
};

export type HygieneEvent = {
  id: string;
  finding_id: string;
  event_type: string;
  from_state?: string;
  to_state?: string;
  actor_user_id?: number;
  reason_code?: string;
  note?: string;
  created_at: string;
};

export type HygieneScan = {
  id: string;
  audit_year_id: string;
  kind: 'rules' | 'ai';
  status: 'queued' | 'running' | 'cancelling' | 'completed' | 'failed' | 'cancelled';
  started_by?: number;
  started_at: string;
  heartbeat_at?: string;
  finished_at?: string;
  items_total: number;
  items_done: number;
  checked_counts: Record<string, number>;
  coverage: Record<string, HygieneCoverage>;
  failed_checks: Record<string, string>;
  model?: string;
  cost_usd?: number;
  error_message?: string;
};

export type HygieneCoverage = { eligible: number; reviewed: number; selected: number };

export type HygieneAreaCount = { area: HygieneArea; high: number; medium: number; low: number };

export type HygieneSummary = {
  high: number;
  medium: number;
  low: number;
  pending_approval: number;
  accepted: number;
  ai_suggestions: number;
  ai_unsure: number;
  fixed_since_last_scan: number;
  fixed_total: number;
  assigned_to_me: number;
  areas: HygieneAreaCount[];
  last_rules_scan?: HygieneScan;
  last_ai_scan?: HygieneScan;
  active_scans: HygieneScan[];
  stale: boolean;
  checked_counts: Record<string, number>;
};

export type HygieneSummaryResponse = {
  summary: HygieneSummary;
  audit_year: { id: string; year: number; name: string; is_locked: boolean };
  /** can_manage / can_approve are false on a locked year; can_run_checks is not (checks still run there). */
  permissions: { can_manage: boolean; can_approve: boolean; can_run_checks: boolean };
  user_id: number;
};

export type HygieneListParams = {
  audit_year_id: string;
  tab: HygieneTab;
  area?: string;
  severity?: string;
  check?: string;
  q?: string;
  page: number;
  page_size: number;
};

export type HygieneListResponse = { items: HygieneFinding[]; total: number; page: number; page_size: number };

export type HygieneCheckMeta = { code: string; area: HygieneArea; severity: HygieneSeverity; module: string; title: string; approximate?: boolean };

export type HygieneMember = { id: number; name: string; role: string };

export type HygieneEstimate = {
  estimate: { items: number; coverage: Record<string, HygieneCoverage>; seconds: number; cost_usd: number };
  item_cap: number;
  runs_today: number;
  daily_limit: number;
};

export type HygieneActionBody = {
  audit_year_id: string;
  owner_user_id?: number | null;
  owner_label?: string;
  reason_code?: string;
  note?: string;
};

function compact<T extends Record<string, unknown>>(params: T): Partial<T> {
  return Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== '')) as Partial<T>;
}

export async function fetchHygieneSummary(auditYearId: string): Promise<HygieneSummaryResponse> {
  const { data } = await axiosInstance.get('/api/hygiene/summary', { params: { audit_year_id: auditYearId } });
  return data;
}

export async function fetchHygieneFindings(params: HygieneListParams): Promise<HygieneListResponse> {
  const { data } = await axiosInstance.get('/api/hygiene/findings', { params: compact(params) });
  return { ...data, items: Array.isArray(data?.items) ? data.items : [] };
}

export async function fetchHygieneFinding(id: string): Promise<{ finding: HygieneFinding; events: HygieneEvent[] }> {
  const { data } = await axiosInstance.get(`/api/hygiene/findings/${id}`);
  return { finding: data.finding, events: Array.isArray(data?.events) ? data.events : [] };
}

export async function fetchHygieneChecks(): Promise<HygieneCheckMeta[]> {
  const { data } = await axiosInstance.get('/api/hygiene/checks');
  return Array.isArray(data?.checks) ? data.checks : [];
}

export async function fetchHygieneAssignees(): Promise<HygieneMember[]> {
  const { data } = await axiosInstance.get('/api/hygiene/assignees');
  return Array.isArray(data?.members) ? data.members : [];
}

export async function postHygieneAction(id: string, action: HygieneAction, body: HygieneActionBody): Promise<HygieneFinding> {
  const { data } = await axiosInstance.post(`/api/hygiene/findings/${id}/${action}`, body);
  return data.finding;
}

export async function startHygieneScan(auditYearId: string, kind: 'rules' | 'ai'): Promise<{ scan: HygieneScan | null; message?: string }> {
  const { data } = await axiosInstance.post('/api/hygiene/scans', { audit_year_id: auditYearId, kind });
  return { scan: data?.scan ?? null, message: data?.message };
}

export async function fetchHygieneScan(id: string): Promise<{ scan: HygieneScan; stalled: boolean }> {
  const { data } = await axiosInstance.get(`/api/hygiene/scans/${id}`);
  return data;
}

export async function cancelHygieneScan(id: string): Promise<HygieneScan> {
  const { data } = await axiosInstance.post(`/api/hygiene/scans/${id}/cancel`, {});
  return data.scan;
}

export async function fetchHygieneAIEstimate(auditYearId: string): Promise<HygieneEstimate> {
  const { data } = await axiosInstance.get('/api/hygiene/ai/estimate', { params: { audit_year_id: auditYearId } });
  return data;
}

export async function downloadHygieneExport(params: Omit<HygieneListParams, 'page' | 'page_size'>, format: 'csv' | 'xlsx', fallbackName = 'hygiene-check'): Promise<{ blob: Blob; filename: string }> {
  let res;
  try {
    res = await axiosInstance.get('/api/hygiene/export', { params: { ...compact(params), format }, responseType: 'blob' });
  } catch (err) {
    // With responseType 'blob' an error body is a Blob too: turn it back into JSON so the server's message shows.
    const data = (err as { response?: { data?: unknown } })?.response?.data;
    if (data instanceof Blob && err && typeof err === 'object') {
      try {
        (err as { response: { data: unknown } }).response.data = JSON.parse(await data.text());
      } catch {
        // not JSON: keep the generic message
      }
    }
    throw err;
  }
  const disposition = String(res.headers?.['content-disposition'] ?? '');
  const match = /filename="([^"]+)"/.exec(disposition);
  return { blob: res.data as Blob, filename: match?.[1] ?? `${fallbackName}.${format}` };
}
